// Brain — v1.1 §3.3, §3.4, §3.5, §3.6, §3.7. Steps 3 and 4.
//
// The shell resolves `/v2/brain/*` here. This module dispatches on the active
// X-axis tab and nothing else; each surface lives in `routes/brain/`. It does
// not open `App.js`, which is 4,963 lines and holds every route in the
// application — that is the whole point of the route-module pattern.
//
// BUILT                              STUBBED (deliberately)
// ─────                              ──────────────────────
// ""        Brain Home               decisions  Pending Decisions
// map       Organization Map
// health    Organization Health (G5, backend E4)
// analytics Operator Analytics (G6, backend E5)
//
// WHY `decisions` IS STUBBED AND MUST STAY THAT WAY
// ================================================
// §3.2 of the build instruction now opens with DO NOT BUILD. The endpoints this
// surface was originally mapped to — `/api/executive/queue`,
// `/api/recommendations/*` — are live and populated, which is exactly what makes
// them dangerous: they serve *campaign and offer* decisions, which §3.6
// explicitly excludes from Pending Decisions. Wiring them would fill the surface
// with the content the spec keeps out, and it would look like it worked. The
// correct source is `/api/organization/tool-requests`, which is empty, and which
// decisions belong here at all is an unsettled Founder ruling.
//
// WHAT MUST BE CARRIED FORWARD — C4
// =================================
// `MIGRATION_MAP.md` calls `components/brain/` an "existing brain mock" with a
// REPLACE verdict. §2.2 supersedes that row: it is 4 screens, 8 panels, 7
// regions, 2 visualisations, a real data layer and 16 live API bindings. This
// lane composes those components (see `brain/BrainHome.jsx`) and modifies none
// of them. They stay mounted on `/brain*` behind REACT_APP_EXEC_BRAIN_ENABLED
// until the Founder retires them, which is F8 and happens after the migration.
//
// X-AXIS GATING (SHELL.md, §3.1)
// ==============================
// Brain Home is open; the other four are founder-only, declared in `areas.js`
// and enforced by the server. A non-founder sees Brain Home alone. The nav
// agreeing with the backend is R5 — the nav ALONE is not access control, which
// is why `OrganizationMap` renders a designed `denied` state for a 403 rather
// than assuming it can never receive one.
import React from "react";

import EmptyState from "../components/EmptyState";
import PageHead from "../components/PageHead";
import { useShell } from "../ShellContext";
import BrainHome from "./brain/BrainHome";
import OperatorAnalytics from "./brain/OperatorAnalytics";
import OrganizationHealth from "./brain/OrganizationHealth";
import OrganizationMap from "./brain/OrganizationMap";
import SkillsMcp from "./brain/SkillsMcp";
import PendingDecisions from "./brain/PendingDecisions";
import StageAPilot from "./brain/StageAPilot";
import "./brain/brain-area.css";

// health and analytics were stubs when this file was first built (see the
// git history for the original copy) — G5/G6 built both against the real
// endpoints (E4 `/organization/health-topology`, E5
// `/operators/{op_id}/analytics`), each in its own module under `brain/`.
// `decisions` stays stubbed; §3.2 says DO NOT BUILD it (see below).

/** Tabs this lane did not build. Copy states what serves them, not a promise. */
const STUBS = {
  decisions: {
    title: "Pending Decisions",
    subtitle: "What Cipher is waiting on you to decide.",
    detail:
      "Blocked, not merely unbuilt. §3.2 says DO NOT BUILD: the endpoints this was mapped to serve campaign and offer decisions, which §3.6 excludes from this surface. The correct source is /api/organization/tool-requests, which is empty. Which decisions belong here is a Founder ruling.",
  },
};

export default function BrainArea() {
  const { tab } = useShell();

  if (tab === "map") return <OrganizationMap />;
  if (tab === "skills") return <SkillsMcp />;
  if (tab === "health") return <OrganizationHealth />;
  if (tab === "analytics") return <OperatorAnalytics />;
  // Still blocked by §3.2 — the surface reads nothing and claims nothing. What
  // changed is that it now STATES the ruling, names the correct source and its
  // emptiness, and shows the evidence contract a decision must satisfy, rather
  // than rendering a generic "not built yet" card.
  if (tab === "decisions") return <PendingDecisions />;
  // REASONING-STAGE-A-OPERATOR-PATH: the reasoning pilot's operator panel.
  if (tab === "stage-a") return <StageAPilot />;
  if (!tab) return <BrainHome />;

  const stub = STUBS[tab];
  if (!stub) {
    // An unknown tab is a routing mismatch, not a missing surface. Saying so is
    // more useful at 3am than silently falling through to Brain Home.
    return (
      <>
        <PageHead title="Brain" subtitle="Unknown view." />
        <EmptyState
          title="No such view"
          detail={`"${tab}" is not a Brain tab. The five are declared in shell/areas.js.`}
        />
      </>
    );
  }

  return (
    <>
      <PageHead title={stub.title} subtitle={stub.subtitle} />
      <EmptyState title="Not built yet" detail={stub.detail} />
    </>
  );
}
